"use client";

import { CheckIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  type ComponentType,
  type ReactNode,
} from "react";

import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import type { Profession } from "@/lib/onboarding/constants";
import { cn } from "@/lib/utils";

import { CONTINUE_CLASS } from "./styles";
import type { AnswersPatch, StepProps } from "./types";

/** The guided onboarding's building blocks. Every step composes these, so
 *  the flow reads as one product and a style change is one edit. Shipped
 *  semantic tokens only (bg-background, bg-primary, border-border…): the
 *  onboarding themes with the rest of the app in light and dark. */

/** Small uppercase label above a step title ("Your certification"). */
function Kicker({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold tracking-[0.12em] text-primary uppercase">
      {children}
    </p>
  );
}

/** Kicker + title + optional body. The title is the step's h1 and receives
 *  focus when the step mounts, so screen readers announce each new screen. */
export function StepHeader({
  kicker,
  title,
  body,
}: {
  kicker?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div className="space-y-2.5">
      {kicker ? <Kicker>{kicker}</Kicker> : null}
      <h1
        ref={ref}
        tabIndex={-1}
        className="font-heading text-[1.75rem] leading-tight font-semibold tracking-tight text-foreground outline-none md:text-4xl"
      >
        {title}
      </h1>
      {body ? (
        <p className="text-base leading-relaxed text-muted-foreground md:text-lg">{body}</p>
      ) : null}
    </div>
  );
}

/** Groups option cards under one accessible name. A plain group, not a
 *  radiogroup: a single-choice card here is a BUTTON that answers and moves
 *  on (advised by the step's "Tap an answer to continue" hint), and a
 *  radiogroup would promise arrow-key selection that doesn't move focus to
 *  the next screen. Multi-select cards are checkboxes. */
export function OptionGroup({
  label,
  children,
  className,
}: {
  /** Accessible name; usually the step title text. */
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3", className)}
    >
      {children}
    </div>
  );
}

/** A tappable answer row: optional icon tile, label, optional description,
 *  and a radio/checkbox mark. At least 60px tall (44px minimum touch target
 *  with room for two lines). */
export function OptionCard({
  label,
  description,
  icon: Icon,
  selected,
  multi = false,
  onSelect,
}: {
  label: ReactNode;
  description?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  selected: boolean;
  multi?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role={multi ? "checkbox" : undefined}
      aria-checked={multi ? selected : undefined}
      aria-pressed={multi ? undefined : selected}
      onClick={onSelect}
      className={cn(
        "flex min-h-15 w-full items-center gap-3.5 rounded-2xl border px-4 py-3.5 text-left transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
        selected
          ? "border-primary bg-primary/10"
          : "border-border bg-card hover:bg-muted/60",
      )}
    >
      {Icon ? (
        <span
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted",
            selected ? "text-primary" : "text-muted-foreground",
          )}
        >
          <Icon className="size-5" />
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-base font-medium text-foreground">{label}</span>
        {description ? (
          <span className="text-sm leading-snug text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
      <SelectionMark selected={selected} multi={multi} />
    </button>
  );
}

function SelectionMark({ selected, multi }: { selected: boolean; multi: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-6 shrink-0 items-center justify-center border-[1.5px]",
        multi ? "rounded-md" : "rounded-full",
        selected
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border",
      )}
    >
      {selected ? <CheckIcon className="size-4" strokeWidth={2.5} /> : null}
    </span>
  );
}

/** A pill-shaped toggle for compact sets (referral sources): a pressed /
 *  unpressed button, so tapping the chosen one again can clear it. */
export function ChoiceChip({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "h-11 rounded-full border px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
        selected
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border bg-card text-muted-foreground hover:bg-muted/60",
      )}
    >
      {label}
    </button>
  );
}

/** The onboarding progress bar, shared by the wizard and the finish route. */
export function ProgressBar({ index, total }: { index: number; total: number }) {
  return (
    <div
      role="progressbar"
      aria-label="Onboarding progress"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={index}
      aria-valuetext={`Step ${index} of ${total}`}
      className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
        style={{ width: `${(index / total) * 100}%` }}
      />
    </div>
  );
}

/** Bottom-of-screen action area. Pushed down by the wizard's flex column
 *  on short steps, and sticky on long ones, so the primary action is always
 *  on screen without scrolling. */
export function StepFooter({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 mt-auto flex flex-col gap-1 bg-background pt-6 pb-[max(env(safe-area-inset-bottom),1.25rem)] md:pb-10">
      {children}
    </div>
  );
}

/** The primary full-width action. */
export function ContinueButton({
  children = "Continue",
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button size="lg" className={CONTINUE_CLASS} {...props}>
      {children}
    </Button>
  );
}

/** Quiet centered helper line ("Tap an answer to continue"). */
export function StepHint({ children }: { children: ReactNode }) {
  return (
    <p className="py-3 text-center text-sm text-muted-foreground">{children}</p>
  );
}

/** Single-choice steps advance on tap, after a beat so the selection
 *  highlight registers before the screen changes. */
const AUTO_ADVANCE_DELAY_MS = 180;

/** Record a single-choice answer now, then advance after
 *  {@link AUTO_ADVANCE_DELAY_MS}. A second tap within the delay replaces the
 *  first; unmounting cancels. */
export function useChooseAndAdvance({
  update,
  advance,
}: Pick<StepProps, "update" | "advance">) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return useCallback(
    (patch: AnswersPatch, profession?: Profession) => {
      update(patch, profession);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => advance(), AUTO_ADVANCE_DELAY_MS);
    },
    [update, advance],
  );
}

/** A numbered row (how it works, the instructor handoff). The number is
 *  decorative: the surrounding <ol> already announces position. */
export function NumberedItem({
  n,
  title,
  children,
}: {
  n: number;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3.5">
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-sm font-semibold text-primary"
      >
        {n}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-base font-semibold text-foreground">{title}</p>
        <p className="text-sm leading-snug text-muted-foreground">{children}</p>
      </div>
    </li>
  );
}

/** A one-question, one-tap screen: header, option cards, and the hint that
 *  advises the user a tap moves on. Role, profession, certification and exam
 *  date are this shape with different data. */
export function SingleChoiceStep<T extends string>({
  kicker,
  title,
  body,
  options,
  selected,
  onPick,
}: {
  kicker: ReactNode;
  title: string;
  body?: ReactNode;
  options: ReadonlyArray<{
    value: T;
    label: ReactNode;
    description?: ReactNode;
    icon?: ComponentType<{ className?: string }>;
  }>;
  selected: T | undefined;
  onPick: (value: T) => void;
}) {
  return (
    <>
      <StepHeader kicker={kicker} title={title} body={body} />
      <OptionGroup label={title} className="mt-7">
        {options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            description={opt.description}
            icon={opt.icon}
            selected={selected === opt.value}
            onSelect={() => onPick(opt.value)}
          />
        ))}
      </OptionGroup>
      <StepFooter>
        <StepHint>Tap an answer to continue</StepHint>
      </StepFooter>
    </>
  );
}

/** The page frame for every onboarding screen. A phone gets the full-height
 *  column it always had; from `md` up the flow sits in a centered card on a
 *  tinted page, under the brand mark, instead of a phone-width strip on a
 *  desktop. `wide` is for the paywall, whose plan grid needs the room. */
export function OnboardingFrame({
  wide = false,
  children,
}: {
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col md:items-center md:justify-center md:px-6 md:py-10">
      <div className="mb-6 hidden items-center gap-2.5 md:flex" aria-hidden="true">
        <Logo size={28} />
        <span className="text-base font-semibold tracking-tight text-foreground">
          Medceptor
        </span>
      </div>
      <div
        className={cn(
          "mx-auto flex w-full max-w-md flex-1 flex-col px-4 pt-[max(env(safe-area-inset-top),1rem)]",
          "md:min-h-[min(46rem,calc(100svh-9rem))] md:flex-none md:rounded-3xl md:border md:border-border md:bg-background md:px-12 md:pt-10 md:shadow-sm",
          wide ? "md:max-w-5xl" : "md:max-w-3xl",
        )}
      >
        {children}
      </div>
    </div>
  );
}
